import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getArticleById } from "../services/api";
import Sidebar    from "../components/Sidebar";
import ChatWidget from "../components/ChatWidget";
import PageHero   from "../components/PageHero";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-PE", {
    year: "numeric", month: "long", day: "numeric",
  });
}

function normalizeUrl(val) {
  if (!val || typeof val !== "string" || !val.trim()) return null;
  return val.trim();
}

export default function ArticleDetail() {
  const { id }                = useParams();
  const navigate              = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [pdfFail, setPdfFail] = useState(false);

  useEffect(() => {
    setLoading(true);
    setPdfFail(false);
    getArticleById(id)
      .then(setArticle)
      .catch(() => setError("No se pudo cargar el artículo."))
      .finally(() => setLoading(false));
  }, [id]);

  const pdfUrl  = normalizeUrl(article?.pdf_url);
  const content = normalizeUrl(article?.content);

  const metadata = [
    article?.author_name && `Autor: ${article.author_name}`,
    article?.created_at  && `Fecha: ${formatDate(article.created_at)}`,
    pdfUrl               && `PDF disponible: ${pdfUrl}`,
  ].filter(Boolean).join(" · ");

  return (
    <div className="page">
      <PageHero
        section="articulos"
        title={loading ? "Cargando..." : (article?.title ?? "Artículo")}
        backBtn={{ label: "Volver", onClick: () => navigate("/articulos") }}
      />

      <div className="two-col">
        <main>
          {error && <p style={{ color: "#c0392b" }}>{error}</p>}

          {loading && (
            <div className="skel">
              {[65, 35, 100, 95, 88, 70].map((w, i) => (
                <div key={i} className="skel-line"
                  style={{ width: `${w}%`, height: i === 0 ? 18 : 12, marginBottom: 8 }} />
              ))}
            </div>
          )}

          {!loading && article && (
            <div className="card">
              <h2 style={{
                fontFamily: "var(--font-display)", fontWeight: 800,
                fontSize: "2rem", color: "var(--teal-dark)",
                marginBottom: ".4rem", lineHeight: 1.35,
              }}>
                {article.title}
              </h2>

              <div style={{
                fontSize: "0.75rem", color: "var(--text-light)",
                marginBottom: "1rem", borderBottom: "0.5px solid var(--border)",
                paddingBottom: ".8rem", display: "flex", gap: "1rem",
                flexWrap: "wrap", alignItems: "center",
              }}>
                {article.created_at && <span>Publicado el {formatDate(article.created_at)}</span>}
                {article.author_name && <span>· {article.author_name}</span>}
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      background: "var(--teal)", color: "#fff",
                      fontFamily: "var(--font-display)", fontWeight: 700,
                      fontSize: "0.72rem", padding: "3px 12px",
                      borderRadius: 20, textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    📄 Abrir PDF
                  </a>
                )}
              </div>

              {pdfUrl && (
                <div style={{
                  marginBottom: "1.5rem", borderRadius: 8, overflow: "hidden",
                  border: "0.5px solid var(--border)", background: "#f5f5f3",
                  width: "100%", lineHeight: 0,
                }}>
                  {!pdfFail ? (
                    <iframe
                      src={pdfUrl}
                      style={{ width: "100%", height: "80vh", minHeight: 480, border: "none", display: "block", maxWidth: "none" }}
                      title="Documento PDF"
                      onError={() => setPdfFail(true)}
                    />
                  ) : (
                    <div style={{ padding: "2rem", textAlign: "center" }}>
                      <p style={{ fontFamily: "var(--font-display)", fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "1rem" }}>
                        Tu navegador no puede mostrar el PDF aquí directamente.
                      </p>
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: "var(--teal)", color: "#fff",
                          fontFamily: "var(--font-display)", fontWeight: 700,
                          fontSize: "0.82rem", padding: "8px 20px",
                          borderRadius: 6, textDecoration: "none",
                        }}
                      >
                        📄 Abrir PDF en nueva pestaña
                      </a>
                    </div>
                  )}
                </div>
              )}

              {content && (
                <div
                  style={{ fontSize: "0.92rem", color: "var(--text)", lineHeight: 1.85 }}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}

              {!content && !pdfUrl && (
                <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
                  Este artículo no tiene contenido disponible.
                </p>
              )}
            </div>
          )}
        </main>

        <Sidebar />
      </div>

      {!loading && article && (
        <ChatWidget
          pageType="article"
          title={article.title ?? ""}
          content={content ?? ""}
          pdfUrl={pdfUrl}
          metadata={metadata}
        />
      )}
    </div>
  );
}